// JobManager.js
import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../api/api';
import { DataTable } from '../components/tables';
import { DataForm } from '../components/forms';
import Toast from '../components/Toast';
import './JobManager.css';

export default function JobManager() {
  const [jobCodes, setJobCodes] = useState([]);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [toastMessage, setToastMessage] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // DataTable specific states
  const [tableColumns, setTableColumns] = useState([]);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [role, setRole] = useState(localStorage.getItem('role') || '');
  
  // Filter states
  const [columnFilters, setColumnFilters] = useState({});
  const [filteredJobCodes, setFilteredJobCodes] = useState([]);

  const tableName = 'JobCode';

  // Load column configuration
  useEffect(() => {
    const fetchColumnConfig = async () => {
      setIsLoadingConfig(true);
      try {
        const allConfigs = await apiFetch('/api/config/columns');
        const jobCodeConfig = Array.isArray(allConfigs) 
            ? allConfigs.find(config => config.tableName === tableName) 
            : null;

        if (jobCodeConfig && Array.isArray(jobCodeConfig.columns)) {
          const sortedColumns = jobCodeConfig.columns.sort((a, b) => a.order - b.order);
          setTableColumns(sortedColumns);
        } else {
          console.error(`CRITICAL: '${tableName}' table configuration not found or is malformed via API.`);
          setTableColumns([]);
          setToastMessage(`Failed to load table column configuration for ${tableName}.`);
          setToastVisible(true);
        }
      } catch (err) {
        console.error('Fetch column config error:', err);
        setToastMessage('Failed to fetch table column configuration: ' + (err.message || 'Unknown error'));
        setToastVisible(true);
        setTableColumns([]);
      }
      setIsLoadingConfig(false);
    };
    
    fetchColumnConfig();
  }, []);

  // Filter job codes based on column filters
  const applyFilters = useCallback((jobCodesData, filters) => {
    if (Object.keys(filters).length === 0) return jobCodesData;
    
    return jobCodesData.filter(jobCode => {
      return Object.entries(filters).every(([columnName, filterConfig]) => {
        const cellValue = jobCode[columnName];
        const { type, value, operator } = filterConfig;
        
        if (!value) return true; // No filter value
        
        // Handle null/undefined values
        if (cellValue === null || cellValue === undefined) {
          return value.toLowerCase() === 'null' || value.toLowerCase() === 'empty';
        }
        
        const stringValue = String(cellValue).toLowerCase();
        const filterValue = String(value).toLowerCase();
        
        switch (type) {
          case 'exact':
            return stringValue === filterValue;
            
          case 'wildcard':
            const regexPattern = filterValue.replace(/\*/g, '.*').replace(/%/g, '.*');
            const regex = new RegExp(`^${regexPattern}$`, 'i');
            return regex.test(stringValue);
            
          case 'numeric':
            const numValue = parseFloat(cellValue);
            const numFilter = parseFloat(value);
            if (isNaN(numValue) || isNaN(numFilter)) return false;
            
            switch (operator) {
              case '<': return numValue < numFilter;
              case '>': return numValue > numFilter;
              case '=': return numValue === numFilter;
              case '<=': return numValue <= numFilter;
              case '>=': return numValue >= numFilter;
              default: return numValue === numFilter;
            }
            
          case 'date':
            const dateValue = new Date(cellValue);
            if (isNaN(dateValue.getTime())) return false;
            
            if (operator === 'range') {
              const [startDate, endDate] = value.split(' to ').map(d => new Date(d));
              return dateValue >= startDate && dateValue <= endDate;
            } else if (operator === 'year') {
              return dateValue.getFullYear().toString() === filterValue;
            } else if (operator === 'month') {
              const yearMonth = `${dateValue.getFullYear()}-${String(dateValue.getMonth() + 1).padStart(2, '0')}`;
              return yearMonth === filterValue;
            } else {
              const filterDate = new Date(value);
              return dateValue.toDateString() === filterDate.toDateString();
            }
            
          default:
            return stringValue.includes(filterValue);
        }
      });
    });
  }, []);

  const loadJobCodes = useCallback(async () => {
    try {
      const data = await apiFetch(`/api/db/${tableName}`);
      const jobCodesData = Array.isArray(data) ? data : [];
      setJobCodes(jobCodesData);
      
      // Apply current filters to new data
      const filtered = applyFilters(jobCodesData, columnFilters);
      setFilteredJobCodes(filtered);
      
      return jobCodesData;
    } catch (err) {
      console.error('Failed to load job codes', err);
      setToastMessage('Failed to load job codes: ' + (err.message || 'Unknown error'));
      setToastVisible(true);
      return [];
    }
  }, [columnFilters, applyFilters]);

  useEffect(() => {
    loadJobCodes();
  }, [loadJobCodes]);

  // DataTable callback handlers
  const handleEdit = useCallback(async (jobCode) => {
    setFormData(jobCode);
    setEditId(jobCode.JobCodeID);
    setShowForm(true);
  }, []);

  const handleDelete = useCallback(async (jobCode) => {
    if (!window.confirm('Are you sure you want to delete this job code?')) return;
    
    try {
      await apiFetch(`/api/db/${tableName}/${jobCode.JobCodeID}`, { method: 'DELETE' });
      setToastMessage('Job code deleted successfully');
      setToastVisible(true);
      await loadJobCodes();
    } catch (err) {
      console.error('Delete failed', err);
      setToastMessage('Delete failed: ' + (err.message || 'Unknown error'));
      setToastVisible(true);
    }
  }, [loadJobCodes]);

  const handleAdd = () => {
    setFormData({});
    setEditId(null);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({});
    setEditId(null);
  };

  // Filter handlers
  const handleColumnFilter = useCallback((columnName, filterConfig) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnName]: filterConfig
    }));
  }, []);

  const clearColumnFilter = useCallback((columnName) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[columnName];
      return newFilters;
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setColumnFilters({});
  }, []);

  // Apply filters when columnFilters change
  useEffect(() => {
    const filtered = applyFilters(jobCodes, columnFilters);
    setFilteredJobCodes(filtered);
  }, [jobCodes, columnFilters, applyFilters]);

  return (
    <div className="job-manager">
      <div className="header-section">
        <div className="header-title">
          <h2>Job Code Manager</h2>
          {/* Add Button - Show only when not editing */}
          {(role === 'admin' || role === 'read-write') && 
           tableColumns.length > 0 && 
           !showForm && (
            <button className="add-button-header" onClick={handleAdd}>Add Job Code</button>
          )}
        </div>
        <div className="header-controls">
          {Object.keys(columnFilters).length > 0 && (
            <button className="clear-filters-button" onClick={clearAllFilters}>
              Clear Filters ({Object.keys(columnFilters).length})
            </button>
          )}
        </div>
      </div>

      {/* Data Display */}
      {tableColumns.length > 0 ? (
        <DataTable
          data={Object.keys(columnFilters).length > 0 ? filteredJobCodes : jobCodes}
          columns={tableColumns}
          role={role}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onDataChange={loadJobCodes}
          title="Job Code List"
          tableName={tableName}
          itemsPerPage={25}
          // Filter props
          onColumnFilter={handleColumnFilter}
          onClearColumnFilter={clearColumnFilter}
          columnFilters={columnFilters}
        />
      ) : !isLoadingConfig && <p>Job Code table configuration could not be loaded. Cannot display job code data.</p>}

      {/* DataForm Modal */}
      {showForm && (
        <DataForm
          data={formData}
          tableName={tableName}
          title={editId ? 'Edit Job Code' : 'Add New Job Code'}
          skipAutoSave={true}
          onSave={async (data) => {
            setIsSaving(true);
            try {
              const method = editId ? 'PUT' : 'POST';
              const url = editId ? `/api/db/${tableName}/${editId}` : `/api/db/${tableName}`;
              
              // For new job codes, ensure JobCodeID is not included in the payload
              const payload = { ...data };
              if (!editId) {
                delete payload.JobCodeID;
              } else {
                payload.JobCodeID = editId;
              }

              await apiFetch(url, {
                method,
                body: JSON.stringify(payload)
              });

              // Refresh the job codes list
              await loadJobCodes();
              
              setToastMessage('Job code saved successfully!');
              setToastVisible(true);
              handleCancel();
            } catch (err) {
              console.error('Save failed:', err);
              setToastMessage('Save failed: ' + (err.message || 'Unknown error'));
              setToastVisible(true);
              throw err; // Re-throw so DataForm can handle error display
            } finally {
              setIsSaving(false);
              setTimeout(() => setToastVisible(false), 3000);
            }
          }}
          onCancel={handleCancel}
        />
      )}

      {toastVisible && (
        <Toast message={toastMessage} onClose={() => setToastVisible(false)} />
      )}
    </div>
  );
}
